import { useTranslation } from 'react-i18next';
import { useAppStore } from '../../store/useAppStore';
import { useSettingsStore } from '../../store/useSettingsStore';
import { requestNotificationPermission } from '../../utils/pwa';
import { useRole } from '../../hooks/useRole';
import { useMutationFeedback } from '../../hooks/useMutationFeedback';
import MenuTabs from './MenuTabs';
import SpecialsTab from './SpecialsTab';
import PersonalizedTab from './PersonalizedTab';
import CustomerDetails from './CustomerDetails';

function OrderForm() {
  const { t } = useTranslation();
  const role = useRole();
  const form = useSettingsStore((state) => state.form);
  const { run } = useMutationFeedback();
  const resetForm = useAppStore((state) => state.resetForm);
  const createOrder = useAppStore((state) => state.createOrder);
  const editOrder = useAppStore((state) => state.editOrder);
  const editingOrderId = useAppStore((state) => state.editingOrderId);
  const cancelEditOrder = useAppStore((state) => state.cancelEditOrder);
  const orders = useAppStore((state) => state.orders);
  const setMode = useSettingsStore((state) => state.setMode);

  const editingOrder = editingOrderId
    ? orders.find((order) => order.id === editingOrderId)
    : null;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (role === 'anonymous') {
      return;
    }
    const customToppings = form.customToppings
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

    if (editingOrderId) {
      await run(async () => {
        await editOrder(editingOrderId, {
          toppings: form.toppings,
          customToppings,
          comment: form.comment,
        });
        cancelEditOrder();
        setMode('orders');
      }, t('msg.orderEdited'));
      return;
    }

    await run(async () => {
      await createOrder({
        customerName: form.customerName,
        toppings: form.toppings,
        customToppings,
        comment: form.comment,
        half: form.half,
        offer: form.offer || null,
      });
      requestNotificationPermission();
      resetForm();
      setMode('orders');
    }, t('msg.orderCreated'));
  };

  const handleCancel = () => {
    cancelEditOrder();
    setMode('orders');
  };

  return (
    <form className="space-y-4" onSubmit={handleSubmit}>
      {editingOrder && (
        <div className="flex items-center justify-between rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-2.5">
          <span className="text-sm font-semibold theme-accent">
            {editingOrder.orderNo ? `#${editingOrder.orderNo} ` : ''}{t('edit.title')}
          </span>
          <button
            type="button"
            className="rounded-md border border-[color:var(--app-border)] px-3 py-1.5 text-sm transition hover:border-emerald-500/60"
            onClick={handleCancel}
          >
            {t('edit.cancel')}
          </button>
        </div>
      )}
      {role === 'anonymous' && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-2.5 text-sm theme-accent-warm">
          {t('menu.joinSession')}
        </div>
      )}
      <MenuTabs />
      {form.menuTab === 'specials' ? <SpecialsTab /> : <PersonalizedTab />}
      <CustomerDetails editing={!!editingOrder} />
    </form>
  );
}

export default OrderForm;
